import { openDB, type DBSchema, type IDBPDatabase } from 'idb';

interface TradePrismaDB extends DBSchema {
  visits: {
    key: string;
    value: {
      id: string;
      store_id: string;
      store_name: string;
      check_in_at: string;
      check_in_lat: number;
      check_in_lng: number;
      status: string;
      synced: boolean;
      created_at: string;
    };
    indexes: { 'by-synced': string };
  };
  photos: {
    key: string;
    value: {
      id: string;
      visit_id: string;
      blob: Blob;
      type: string;
      category: string;
      synced: boolean;
      created_at: string;
      // Anti-fraud integrity fields (optional for backward compat)
      integrity_hash?: string;
      device_nonce?: number;
      device_fingerprint?: string;
      latitude?: number;
      longitude?: number;
      capture_metadata?: Record<string, any>;
    };
    indexes: { 'by-visit': string; 'by-synced': string };
  };
  forms: {
    key: string;
    value: {
      id: string;
      visit_id: string;
      type: string;
      data: Record<string, any>;
      synced: boolean;
      created_at: string;
    };
    indexes: { 'by-visit': string; 'by-synced': string };
  };
  sync_queue: {
    key: string;
    value: {
      id: string;
      method: string;
      url: string;
      data?: any;
      headers?: Record<string, string>;
      retries: number;
      max_retries: number;
      created_at: string;
    };
  };
  location_pings: {
    key: string;
    value: {
      id: string;
      latitude: number;
      longitude: number;
      accuracy: number | null;
      battery_level: number | null;
      speed: number | null;
      timestamp: string;
      synced: number; // 0 = unsynced, 1 = synced (IndexedDB indexes don't support boolean)
    };
    indexes: { 'by-synced': number; 'by-timestamp': string };
  };
}

const DB_NAME = 'trade-prisma';
const DB_VERSION = 3;

let dbPromise: Promise<IDBPDatabase<TradePrismaDB>> | null = null;

export function getDB(): Promise<IDBPDatabase<TradePrismaDB>> {
  if (!dbPromise) {
    dbPromise = openDB<TradePrismaDB>(DB_NAME, DB_VERSION, {
      upgrade(db) {
        // Visits store
        if (!db.objectStoreNames.contains('visits')) {
          const visitStore = db.createObjectStore('visits', { keyPath: 'id' });
          visitStore.createIndex('by-synced', 'synced');
        }

        // Photos store
        if (!db.objectStoreNames.contains('photos')) {
          const photoStore = db.createObjectStore('photos', { keyPath: 'id' });
          photoStore.createIndex('by-visit', 'visit_id');
          photoStore.createIndex('by-synced', 'synced');
        }

        // Forms store
        if (!db.objectStoreNames.contains('forms')) {
          const formStore = db.createObjectStore('forms', { keyPath: 'id' });
          formStore.createIndex('by-visit', 'visit_id');
          formStore.createIndex('by-synced', 'synced');
        }

        // Sync queue
        if (!db.objectStoreNames.contains('sync_queue')) {
          db.createObjectStore('sync_queue', { keyPath: 'id' });
        }

        // Location pings (v2)
        if (!db.objectStoreNames.contains('location_pings')) {
          const pingStore = db.createObjectStore('location_pings', { keyPath: 'id' });
          pingStore.createIndex('by-synced', 'synced');
          pingStore.createIndex('by-timestamp', 'timestamp');
        }
      },
    });
  }
  return dbPromise;
}

export async function clearAllData(): Promise<void> {
  const db = await getDB();
  const tx = db.transaction(['visits', 'photos', 'forms', 'sync_queue', 'location_pings'], 'readwrite');
  await Promise.all([
    tx.objectStore('visits').clear(),
    tx.objectStore('photos').clear(),
    tx.objectStore('forms').clear(),
    tx.objectStore('sync_queue').clear(),
    tx.objectStore('location_pings').clear(),
    tx.done,
  ]);
}
